import type { AxiosRequestConfig } from 'axios';
import api from './api';
import type { BadgeContent, CalibrationLabels } from './badgePrint';

/**
 * A pult Zebra nyomtatója a backend nyomtatási során és a helyszíni print
 * agenten át (#178).
 *
 * A böngészős út (`badgePrint.ts`, `window.print()`) tartaléknak megmarad: ha
 * egyetlen ügynök sem online, a pult automatikusan arra esik vissza. Ez a modul
 * csak a HTTP-oldalt és az állapot értelmezését adja; a választás a
 * `useLabelPrinter` hooké.
 */

/** Az ügynök által a `~HS` válaszból jelentett nyomtató-állapot. */
export interface PrinterReport {
  online?: boolean;
  ready?: boolean;
  supportsStatus?: boolean;
  paperOut?: boolean;
  paused?: boolean;
  headOpen?: boolean;
  ribbonOut?: boolean;
  bufferFull?: boolean;
  error?: string;
  printerName?: string;
  /** A nyomtató címe, ahogy az agent eléri (`192.168.88.50:9100`). */
  printerHost?: string;
  dryRun?: boolean;
}

export interface PrintAgent {
  id: string;
  name: string;
  online: boolean;
  lastSeenAt: string | null;
  printerStatus: PrinterReport | null;
  transport: string | null;
  agentVersion: string | null;
  createdAt: string;
  /** A nyomtató címe a pult hálózatán (pl. `192.168.88.50`), a Beállításban megadva. */
  printerHost?: string | null;
  /** A nyomtatóra várakozó címkék száma. */
  queued?: number;
}

export interface PrinterStatusResponse {
  agents: PrintAgent[];
  queued: number;
}

export type PrintJobStatus = 'QUEUED' | 'SENT' | 'PRINTED' | 'FAILED' | 'CANCELLED' | 'EXPIRED';

export interface PrintJobResult {
  id: string;
  status: PrintJobStatus;
  label: string;
  error: string | null;
}

/**
 * A pult jelzőjének egyetlen állapota, a legsúlyosabb nyer.
 *
 * - `unknown`: az állapot még nem jött meg — ilyenkor még nem nyomtatunk
 * - `none`: nincs bekötött nyomtató — a böngészős nyomtatás megy
 * - `unselected`: több nyomtató van, és ez a gép még nem választott
 * - `offline`: a kiválasztott nyomtató ügynöke nem jelentkezett mostanában
 * - `disconnected`: az ügynök él, de a nyomtatót nem éri el (kihúzták, kikapcsolták)
 * - `paperOut` / `headOpen` / `paused`: a nyomtató saját hibajelzése
 * - `ready`: nyomtatásra kész
 */
export type PrinterState =
  | 'unknown'
  | 'none'
  | 'unselected'
  | 'offline'
  | 'disconnected'
  | 'paperOut'
  | 'headOpen'
  | 'paused'
  | 'ready';

/**
 * Melyik nyomtatóra nyomtat ez a gép (#178, két nyomtató a pulton).
 *
 * A gép választása nyer, ha az a nyomtató még létezik. Választás nélkül csak
 * akkor döntünk helyette, ha pontosan egy nyomtató van — kettőnél egy tipp a
 * másik pult sorába küldené a címkét.
 */
export function selectedAgent(
  status: PrinterStatusResponse | null,
  selectedId: string | null
): PrintAgent | null {
  const agents = status?.agents ?? [];
  const chosen = selectedId ? agents.find((agent) => agent.id === selectedId) : undefined;
  if (chosen) return chosen;
  return agents.length === 1 ? agents[0] : null;
}

/** Egy nyomtató állapota az ügynöke jelentéséből. */
export function agentState(agent: PrintAgent): PrinterState {
  if (!agent.online) return 'offline';
  const report = agent.printerStatus ?? {};
  if (report.online === false) return 'disconnected';
  if (report.paperOut) return 'paperOut';
  if (report.headOpen) return 'headOpen';
  if (report.paused) return 'paused';
  return 'ready';
}

/** A pult jelzőjének állapota: a KIVÁLASZTOTT nyomtatóé. */
export function printerState(
  status: PrinterStatusResponse | null,
  selectedId: string | null = null
): PrinterState {
  if ((status?.agents ?? []).length === 0) return 'none';
  const agent = selectedAgent(status, selectedId);
  return agent ? agentState(agent) : 'unselected';
}

/** Egy sikertelen közvetlen nyomtatás — a `reason` a pult szótárának kulcsát adja. */
export class LabelPrintError extends Error {
  readonly reason: 'failed' | 'notPickedUp' | 'unconfirmed' | 'notReady' | 'noPrinterSelected';

  readonly printerMessage?: string;

  constructor(reason: LabelPrintError['reason'], printerMessage?: string) {
    super(`Label printing ${reason}${printerMessage ? `: ${printerMessage}` : ''}`);
    this.name = 'LabelPrintError';
    this.reason = reason;
    this.printerMessage = printerMessage;
  }
}

/** A pult hibaüzenetének kulcsa; `null`, ha a hiba nem a közvetlen nyomtatásé. */
export type LabelPrintErrorKey =
  | 'checkin.printerNotPickedUp'
  | 'checkin.printerUnconfirmed'
  | 'checkin.printerJobFailed'
  | 'checkin.printerNotReady'
  | 'checkin.printerNoneSelected';

export function labelPrintErrorKey(error: unknown): LabelPrintErrorKey | null {
  if (!(error instanceof LabelPrintError)) return null;
  if (error.reason === 'notPickedUp') return 'checkin.printerNotPickedUp';
  if (error.reason === 'unconfirmed') return 'checkin.printerUnconfirmed';
  if (error.reason === 'notReady') return 'checkin.printerNotReady';
  if (error.reason === 'noPrinterSelected') return 'checkin.printerNoneSelected';
  return 'checkin.printerJobFailed';
}

export async function fetchPrinterStatus(eventId: string): Promise<PrinterStatusResponse> {
  // Csendes: a pult 5 másodpercenként kérdezi, és egy kieső backend mellett a
  // közös interceptor kérésenként dobna egy toastot a pult gombjaira.
  const response = await api.get('/printing/status', {
    params: { eventId },
    silentError: true,
  } as AxiosRequestConfig);
  const data = response.data as Partial<PrinterStatusResponse> | undefined;
  return { agents: Array.isArray(data?.agents) ? data.agents : [], queued: data?.queued ?? 0 };
}

export async function listPrintAgents(eventId: string): Promise<PrintAgent[]> {
  const response = await api.get('/printing/agents', { params: { eventId } });
  return Array.isArray(response.data) ? response.data : [];
}

export async function createPrintAgent(
  eventId: string,
  name: string,
  printerHost?: string
): Promise<{ agent: PrintAgent; token: string }> {
  const response = await api.post('/printing/agents', {
    eventId,
    name,
    ...(printerHost ? { printerHost } : {}),
  });
  return response.data;
}

/** Új token egy meglévő ügynöknek; a régi érvényét veszti. */
export async function rotatePrintAgentToken(
  agentId: string
): Promise<{ agent: PrintAgent; token: string }> {
  const response = await api.post(`/printing/agents/${agentId}/token`);
  return response.data;
}

/**
 * A nyomtató nevének és/vagy címének módosítása. A gépek választása az
 * azonosítóhoz kötött, átnevezés után is megmarad. Üres cím = törlés.
 */
export async function updatePrintAgent(
  agentId: string,
  patch: { name?: string; printerHost?: string }
): Promise<PrintAgent> {
  const response = await api.patch(`/printing/agents/${agentId}`, patch);
  return response.data;
}

export async function revokePrintAgent(agentId: string): Promise<void> {
  await api.delete(`/printing/agents/${agentId}`);
}

export type JobPayload =
  | { kind: 'BADGE' | 'TEST'; badge: BadgeContent; ticketId?: string }
  | { kind: 'CALIBRATION'; calibration: CalibrationLabels };

/** A nyomtatóválasztás gépenként, a böngészőben megjegyezve. */
export const PRINTER_CHOICE_STORAGE_KEY = 'eventbot.checkin.printerAgentId';

export function loadPrinterChoice(): string | null {
  try {
    return localStorage.getItem(PRINTER_CHOICE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function savePrinterChoice(agentId: string | null): void {
  try {
    if (agentId) localStorage.setItem(PRINTER_CHOICE_STORAGE_KEY, agentId);
    else localStorage.removeItem(PRINTER_CHOICE_STORAGE_KEY);
  } catch {
    // Privát ablakban nincs tároló: a választás a lap élettartamáig él.
  }
}

/**
 * Egy címke a Zebrára. A szerver megvárja az eredményt, tehát ez az ígéret
 * akkor teljesül, ha a címke KIMENT — ugyanaz a szerződés, mint a böngészős
 * `printBadge`-é, és a pult erre építi, hogy csak sikeres nyomtatás után jelöl
 * érkezettnek.
 */
export async function printViaAgent(
  eventId: string,
  agentId: string,
  payload: JobPayload
): Promise<void> {
  const body =
    payload.kind === 'CALIBRATION'
      ? { eventId, agentId, kind: payload.kind, calibration: payload.calibration }
      : {
          eventId,
          agentId,
          kind: payload.kind,
          badge: { ...payload.badge, type: payload.badge.type || undefined },
          ticketId: payload.ticketId,
        };
  const response = await api.post('/printing/jobs', body);
  const job = response.data as PrintJobResult;

  if (job.status === 'PRINTED') return;
  if (job.status === 'FAILED') throw new LabelPrintError('failed', job.error ?? undefined);
  if (job.status === 'SENT') throw new LabelPrintError('unconfirmed');
  throw new LabelPrintError('notPickedUp');
}
